import { NullableDateInput, stringifyDate } from '@superdispatch/dates';
import { z } from 'zod';

function parseBoolean(value: unknown) {
  return typeof value === 'string' || typeof value === 'number'
    ? /^(true|1)$/i.test(String(value))
    : value;
}

function parseNumber(value: unknown) {
  return value == null || value === '' ? null : Number(value);
}

export type GeneralSettingsDTO = z.infer<typeof generalSettingsSchema>;
export const generalSettingsSchema = z.object({
  inbox: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  timezone: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  initial_load_id: z.preprocess(
    parseNumber,
    z.number().nullable().default(null).catch(null),
  ),
  multiple_units_bol: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
});

export const generalSettingsEditSchema = z.object({
  inbox: z
    .unknown()
    .optional()
    .transform(() => undefined),
  timezone: z
    .string()
    .nullish()
    .transform((value) => value ?? '')
    .pipe(z.string().min(1, 'This field is required'))
    .prefault(''),
  initial_load_id: z.preprocess(
    parseNumber,
    z.number().nullable().default(null).catch(null),
  ),
  multiple_units_bol: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  theme_mode: z.enum(['light', 'dark', 'system']).catch('light'),
});

const requiredInspectionFieldsSchema = z.object({
  odometer: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  is_drivable: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  windscreen_present: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  all_glasses_ok: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  title_present: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  valet_keys_count: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  remotes_count: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  headrests_count: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  cargo_cover_present: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  spare_tire_present: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  radio_present: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  manuals_present: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  navigation_disk_present: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  plugin_charger_cable_present: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  headphones_present: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
});

export type DriverAppSettingsDTO = z.infer<typeof driverAppSettingsSchema>;
export const driverAppSettingsSchema = z
  .object({
    fleet_mode: z.preprocess(
      parseBoolean,
      z.boolean().nullable().default(false).catch(false),
    ),
    hide_pricing: z.preprocess(
      parseBoolean,
      z.boolean().nullable().default(false).catch(false),
    ),
    is_mobile_loadboard_enabled: z.preprocess(
      parseBoolean,
      z.boolean().nullable().default(false).catch(false),
    ),
    is_customer_signature_required: z.preprocess(
      (value) => parseBoolean(value ?? true),
      z.boolean().nullable().default(false).catch(false),
    ),
    is_subject_to_inspection_disabled: z.preprocess(
      parseBoolean,
      z.boolean().nullable().default(false).catch(false),
    ),
    enabled_driver_pay: z.preprocess(
      (value) => parseBoolean(value ?? true),
      z.boolean().nullable().default(false).catch(false),
    ),
    drivers_can_edit_carrier_info: z.preprocess(
      (value) => parseBoolean(value ?? true),
      z.boolean().nullable().default(false).catch(false),
    ),
    drivers_can_rate: z.preprocess(
      parseBoolean,
      z.boolean().nullable().default(false).catch(false),
    ),
    drivers_can_mark_vehicles_as_cannot_be_picked_up: z.preprocess(
      parseBoolean,
      z.boolean().nullable().default(false).catch(false),
    ),
    drivers_can_view_shipper_attachments: z.preprocess(
      parseBoolean,
      z.boolean().nullable().default(false).catch(false),
    ),
    arrived_at_location_min_range_in_miles: z.preprocess(
      parseNumber,
      z.number().nullable().default(null).catch(null),
    ),
    is_driver_mark_as_arrived_enabled: z.preprocess(
      parseBoolean,
      z.boolean().nullable().default(false).catch(false),
    ),
    is_vin_barcode_generation_enabled: z.preprocess(
      parseBoolean,
      z.boolean().nullable().default(false).catch(false),
    ),
    is_aiag_damage_without_photo_enabled: z.preprocess(
      parseBoolean,
      z.boolean().nullable().default(false).catch(false),
    ),
    is_additional_inspection_fields_required: z.preprocess(
      parseBoolean,
      z.boolean().nullable().default(false).catch(false),
    ),
    required_inspection_fields: requiredInspectionFieldsSchema,
    is_clean_bol_enabled: z.preprocess(
      parseBoolean,
      z.boolean().nullable().default(false).catch(false),
    ),
    is_driver_clean_bol_enabled: z.preprocess(
      parseBoolean,
      z.boolean().nullable().default(false).catch(false),
    ),
  })
  .superRefine((values, ctx) => {
    if (!values.is_additional_inspection_fields_required) {
      return;
    }

    const fields = values.required_inspection_fields;

    if (fields && Object.values(fields).some((field) => field === true)) {
      return;
    }

    ctx.addIssue({
      code: 'custom',
      path: ['required_inspection_fields'],
      message: 'At least one inspection field must be selected',
    });
  });

const BCC_EMAIL_MAX_LENGTH = 200;

export type BillingSettingsDTO = z.infer<typeof billingSettingsSchema>;
export const billingSettingsSchema = z
  .object({
    attach_bol: z.preprocess(
      parseBoolean,
      z.boolean().nullable().default(false).catch(false),
    ),
    include_dates_on_bol: z.preprocess(
      parseBoolean,
      z.boolean().nullable().default(false).catch(false),
    ),
    bcc_email: z
      .string()
      .nullish()
      .transform((value) => value ?? ''),
  })
  .superRefine((values, ctx) => {
    const email = values.bcc_email;

    if (!email) {
      return;
    }

    if (!z.email().safeParse(email).success) {
      ctx.addIssue({
        code: 'custom',
        path: ['bcc_email'],
        message: 'Enter a single valid email',
      });
      return;
    }

    if (email.length > BCC_EMAIL_MAX_LENGTH) {
      ctx.addIssue({
        code: 'custom',
        path: ['bcc_email'],
        message: `Maximum length is ${BCC_EMAIL_MAX_LENGTH} characters`,
      });
    }
  });

const EMAIL_LIST_FIELDS = [
  'notification_emails',
  'delivery_confirmation_emails',
] as const;

export function refineNotificationEmails(
  values: Record<(typeof EMAIL_LIST_FIELDS)[number], string[]>,
  ctx: z.core.$RefinementCtx,
) {
  for (const field of EMAIL_LIST_FIELDS) {
    values[field].forEach((email, index) => {
      if (!email) {
        ctx.addIssue({
          code: 'custom',
          path: [field, index],
          message: 'This field is required',
        });
        return;
      }

      if (!z.email().safeParse(email).success) {
        ctx.addIssue({
          code: 'custom',
          path: [field, index],
          message: 'Please make sure all emails are correct.',
        });
      }
    });
  }
}

export type EmailNotificationSettingsDTO = z.infer<
  typeof emailNotificationSettingsSchema
>;
export const emailNotificationSettingsSchema = z
  .object({
    broker_notifications_enabled: z.preprocess(
      parseBoolean,
      z.boolean().nullable().default(false).catch(false),
    ),
    notification_emails: z
      .array(
        z
          .string()
          .nullish()
          .transform((value) => value ?? ''),
      )
      .nullish()
      .transform((value) => value ?? []),
    delivery_confirmation_emails: z
      .array(
        z
          .string()
          .nullish()
          .transform((value) => value ?? ''),
      )
      .nullish()
      .transform((value) => value ?? []),
  })
  .superRefine(refineNotificationEmails);

export type LoadOfferNotificationSettingsDTO = z.infer<
  typeof loadOfferNotificationSettingsSchema
>;
const loadOfferNotificationSettingsSchema = z.object({
  is_load_offer_email_notifications_enabled: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  is_load_offer_sms_notifications_enabled: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  is_superpay_payment_onhold_email_notifications_enabled: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),

  is_valid_load_offer_phone_numbers: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  is_unsubscribed_from_load_offer_sms: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  is_unsubscribed_from_load_offer_emails: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),

  user_email: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  load_offer_email: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  load_offer_phone_numbers: z
    .array(
      z
        .string()
        .nullish()
        .transform((value) => value ?? ''),
    )
    .nullish()
    .transform((value) => value ?? []),
});

export type NotificationSettingsPayloadDTO = z.infer<
  typeof loadOfferNotificationSettingsPayloadSchema
>;
export const loadOfferNotificationSettingsPayloadSchema = z.object({
  is_load_offer_email_notifications_enabled: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  is_load_offer_sms_notifications_enabled: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  is_superpay_payment_onhold_email_notifications_enabled: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
});

export type FactoringSettingsDTO = z.infer<typeof factoringSettingsSchema>;
export const factoringSettingsSchema = z.object({
  name: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  address: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  city: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  state: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  zip: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  email: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  phone_number: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  fee_in_percent: z.preprocess(
    parseNumber,
    z.number().nullable().default(null).catch(null),
  ),
});

export type QuickbookOnlineDTO = z.infer<typeof quickbookOnlineSchema>;
export const quickbookOnlineSchema = z.object({
  connected: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  grant_url: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  connected_at: z.preprocess(
    (value) =>
      stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
    z.string().nullable().default(null),
  ),
  invoice_item: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  is_us_company: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  tax: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  is_allowed_to_create_customer: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  customers_updated_at: z.preprocess(
    (value) =>
      stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
    z.string().nullable().default(null),
  ),
});

const quickbookServiceItemSchema = z.object({
  id: z.preprocess(
    parseNumber,
    z.number().nullable().default(null).catch(null),
  ),
  name: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
});

export type QuickbookOnlineServiceDTO = z.infer<
  typeof quickbookOnlineServiceSchema
>;
export const quickbookOnlineServiceSchema = quickbookServiceItemSchema;

export type QuickbookDesktopDTO = z.infer<typeof quickbookDesktopSchema>;
export const quickbookDesktopSchema = z.object({
  connected: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  connected_at: z.preprocess(
    (value) =>
      stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
    z.string().nullable().default(null),
  ),
  customers_last_updated_at: z.preprocess(
    (value) =>
      stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
    z.string().nullable().default(null),
  ),
  is_allowed_to_create_customer: z.preprocess(
    parseBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  service_items_last_updated_at: z.preprocess(
    (value) =>
      stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
    z.string().nullable().default(null),
  ),
  qwc_url: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  password: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  service_items: z
    .array(quickbookServiceItemSchema)
    .nullish()
    .transform((value) => value ?? []),
});

export type QBOTaxDTO = z.infer<typeof qboTaxSchema>;
export const qboTaxSchema = z.object({
  id: z.preprocess(parseNumber, z.number().nullable().catch(null)),
  name: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  rate: z.preprocess(
    parseNumber,
    z.number().nullable().default(null).catch(null),
  ),
});
